import { forwardRef, type ReactNode, type SelectHTMLAttributes } from "react";

/**
 * Themed `<select>` for the canvas surfaces.
 *
 * Per the project's auto-memory rule on native browser chrome, every
 * `<select>` inside a styled surface MUST set `appearance: none`
 * (with vendor prefixes) and supply its own dropdown affordance. The
 * actual CSS lives in `src/styles/tokens.css` under `.tktic-select`
 * so the styles flow through the standard cascade and can be debugged
 * via DevTools alongside the rest of the design tokens.
 *
 * Don't drop a raw `<select>` into a canvas surface — use this
 * component or apply `className="tktic-select"` directly.
 *
 * Ref forwarding is REQUIRED: React Hook Form's `register()` returns
 * a `ref` that must reach the underlying `<select>` for RHF to read
 * the field's value. Without `forwardRef` the ref is silently
 * dropped onto the function component and the form value never
 * updates — `getValues()` keeps returning the default. Every
 * `<CanvasSelect {...form.register(...)}>` callsite depends on this.
 */

export type CanvasSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  children: ReactNode;
};

export const CanvasSelect = forwardRef<HTMLSelectElement, CanvasSelectProps>(
  function CanvasSelect({ className = "", children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={["tktic-select", className].filter(Boolean).join(" ")}
      >
        {children}
      </select>
    );
  },
);
